import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteVisitRecordingStore } from '@store/siteVisitRecordingStore';
import { startSiteVisitRecording } from '@services/siteVisitRecording/siteVisitRecordingEngine';
import { useSiteVisitRecordingEligibility } from './useSiteVisitRecordingEligibility';
import useSiteLeadStore from '@store/siteLeadStore';

/**
 * `location.key`/`state` survive a hard page reload (the browser preserves `history.state` for the
 * current entry) but a brand-new `navigate()` call (e.g. Overview → consent popup → visit form)
 * always gets a fresh key. We use that to tell a genuine "start a new recording" navigation apart
 * from a reload of a navigation we already handled, so a reload never re-triggers auto-start.
 */
const ATTEMPTED_KEYS_STORAGE_KEY = 'siteVisitRecording:autoStartAttemptedKeys';
const MAX_TRACKED_KEYS = 20;

function readAttemptedKeys(): string[] {
	try {
		const raw = sessionStorage.getItem(ATTEMPTED_KEYS_STORAGE_KEY);
		return raw ? (JSON.parse(raw) as string[]) : [];
	} catch (error) {
		console.warn('Failed to read auto-start attempted keys from session storage:', error);
		return [];
	}
}

function hasAutoStartBeenAttempted(locationKey: string): boolean {
	return readAttemptedKeys().includes(locationKey);
}

function markAutoStartAttempted(locationKey: string): void {
	try {
		const keys = readAttemptedKeys();
		if (!keys.includes(locationKey)) {
			sessionStorage.setItem(
				ATTEMPTED_KEYS_STORAGE_KEY,
				JSON.stringify([...keys, locationKey].slice(-MAX_TRACKED_KEYS))
			);
		}
	} catch (error) {
		console.warn('Failed to save auto-start attempted keys to session storage:', error);
	}
}

/**
 * Starts recording once when the visit flow was opened with `autoStartRecording` in location state.
 * Does not re-run on a page reload of the same navigation — any recording left over from before the
 * reload is instead restored (active session or pending upload) by the recovery hooks, and is only
 * discarded once the user goes through the consent popup again for a genuinely new navigation.
 */
export function useAutoStartSiteVisitRecording(recoveryComplete: boolean): void {
	const { recordingEnabled, siteId } = useSiteVisitRecordingEligibility();
	const hasVisitRecordingConsent = useSiteLeadStore((s) => s.hasVisitRecordingConsent);
	const phase = useSiteVisitRecordingStore((s) => s.phase);
	const { state: locationState, key: locationKey } = useLocation();
	const attemptedKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (!recoveryComplete || attemptedKeyRef.current === locationKey) {
			return;
		}
		if (!hasVisitRecordingConsent || !recordingEnabled || siteId == null || phase !== 'idle') {
			return;
		}
		if (
			!locationState ||
			typeof locationState !== 'object' ||
			!('autoStartRecording' in locationState) ||
			!(locationState as { autoStartRecording?: boolean }).autoStartRecording
		) {
			return;
		}

		attemptedKeyRef.current = locationKey;
		if (hasAutoStartBeenAttempted(locationKey)) {
			return;
		}

		markAutoStartAttempted(locationKey);
		void startSiteVisitRecording(siteId);
	}, [hasVisitRecordingConsent, recordingEnabled, siteId, phase, locationState, locationKey, recoveryComplete]);
}
